import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useEntity } from '@app/hooks/useEntity';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { useLanguageStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, Bullseye, Button, Card, CardBody, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, PageSection, Pagination, PaginationVariant, SearchInput, Split, SplitItem, Title, Toolbar, ToolbarContent, ToolbarItem } from '@patternfly/react-core';
import { SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

/**
 * Languages are reference data, and this page is read only: there is no New, no Edit and no Remove.
 *
 * The list is seeded and fixed, and the columns are load bearing beyond display - a class's
 * language is resolved by `code`, so an edit to it would quietly stop matching. All this page owes
 * anyone is a way to look up what a code stands for, so it offers search and nothing else.
 */

const Languages: React.FunctionComponent = () => {
  // --- Core State ---
  const { items: languages, error, retry } = useEntity(useLanguageStore);

  // --- UI State ---
  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('languages', 10);
  const [filterText, setFilterText] = React.useState('');

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo(() => {
    if (!filterText.trim()) return languages;
    const lowerFilter = filterText.toLowerCase();
    return languages.filter(l => 
      l.name.toLowerCase().includes(lowerFilter) || 
      l.code.toLowerCase().includes(lowerFilter) ||
      l.englishName.toLowerCase().includes(lowerFilter)
    );
  }, [languages, filterText]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // --- UI Helpers ---
  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact={true}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  return (
    <React.Fragment>
      {/* Header Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Languages</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {/* List / Table Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            <Toolbar id="compact-toolbar" clearAllFilters={() => setFilterText('')}>
              <ToolbarContent>
                <ToolbarItem>
                  <SearchInput aria-label="Search languages" placeholder="Search by name or code..." value={filterText} onChange={(_e, val) => { setFilterText(val); setPage(1); }} onClear={() => setFilterText('')} />
                </ToolbarItem>
                <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
              </ToolbarContent>
            </Toolbar>

            {/* gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
            <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
              <InnerScrollContainer>
                <Table aria-label="Languages Table" isStickyHeader variant='compact' gridBreakPoint="">
                  <Thead>
                    <Tr>
                      <Th>Name (Local)</Th>
                      <Th>English Name</Th>
                      <Th>Code</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedRows.map((row) => (
                      <Tr key={row.id}>
                        <Td dataLabel="Name (Local)"><b>{row.name}</b></Td>
                        <Td dataLabel="English Name">{row.englishName}</Td>
                        <Td dataLabel="Code">{row.code}</Td>
                      </Tr>
                    ))}
                    {/* Nothing to show: distinguish "the load failed" from "no records match" */}
                    {error && filteredRows.length === 0 && (
                      <DataLoadErrorRow colSpan={3} error={error} entityLabel="languages" onRetry={retry} />
                    )}
                    {!error && filteredRows.length === 0 && (
                      <Tr>
                        <Td colSpan={3}>
                          <Bullseye>
                            <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No results found">
                              <EmptyStateBody>No languages match your search criteria.</EmptyStateBody>
                              <EmptyStateFooter>
                                <EmptyStateActions><Button variant="link" onClick={() => setFilterText('')}>Clear search</Button></EmptyStateActions>
                              </EmptyStateFooter>
                            </EmptyState>
                          </Bullseye>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </InnerScrollContainer>
            </OuterScrollContainer>
            {buildPagination('bottom')}
          </CardBody>
        </Card>
      </PageSection>
    </React.Fragment>
  );
};

export { Languages };
